/* ─── A1CW · chatbot.js — A1 Air Advisor sitewide widget ───────────────
   Floating, closeable AI chat launcher that loads on EVERY page.
   Mounts on #a1-chatbot-root (defined in layout/theme.liquid).

   Backend: the SAME Cloudflare Worker as the /pages/recommended advisor.
   The Worker owns the system prompt, the 3-strike lockout, and IP
   rate-limiting / blocking. No API key lives here — this is pure frontend.

   - Opt-in consent remembered on device (localStorage).
   - Conversation + open state follow the visitor as they browse
     (sessionStorage); clears when the tab closes.
   - All styles/markup scoped under .a1aw- / #a1aw-* so nothing collides
     with the theme or the full-page advisor section (.a1aa-).
   ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var MOUNT = document.getElementById('a1-chatbot-root');
  if (!MOUNT || MOUNT.getAttribute('data-a1aw-mounted')) return;
  MOUNT.setAttribute('data-a1aw-mounted', '1');

  // ============================================================
  //  Cloudflare Worker proxy — the SAME backend as /recommended.
  // ============================================================
  var A1AW_PROXY_URL = 'https://a1-air-advisor-proxy.ryanrad.workers.dev/';

  var DEFLECTION_LINE = "I'm A1's compressor specialist — I'm only set up to help with compressors, pneumatic tools, and compressed air systems. For anything else, please call our team at (803) 693-2350.";
  var WELCOME_HTML = '<strong>Hey, I\'m A1 Air Advisor</strong> — your AI-powered compressed air expert. I can help you size a new compressor, find the right parts, or troubleshoot your existing unit.<br><br>Just a reminder: I\'m AI, so always confirm critical specs with our team before ordering. What are you working on?';
  var MAX_TURNS = 6;

  // ---------- Persistence keys ----------
  var CONSENT_KEY = 'a1aw_consent';   // localStorage   — opt-in remembered on device
  var HISTORY_KEY = 'a1aw_history';   // sessionStorage — conversation follows navigation
  var OPEN_KEY    = 'a1aw_open';      // sessionStorage — panel open state
  var STRIKES_KEY = 'a1aw_strikes';   // sessionStorage
  var LOCKED_KEY  = 'a1aw_locked';    // sessionStorage

  // ============================================================
  //  Styles (injected once)
  // ============================================================
  var CSS = `
  .a1aw-root {
    --a1-navy:#1F3C88; --a1-navy-dark:#162d6b; --a1-navy-light:#e8ecf7;
    --a1-orange:#E87722; --a1-gray:#f4f5f7; --a1-border:#dde1ea;
    --a1-text:#1a1e2e; --a1-muted:#6b7280; --a1-white:#ffffff;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; color:var(--a1-text);
  }
  .a1aw-root, .a1aw-root *, .a1aw-root *::before, .a1aw-root *::after { box-sizing:border-box; }

  .a1aw-launcher {
    position:fixed; bottom:22px; right:22px; width:60px; height:60px; border-radius:50%;
    background:var(--a1-navy); border:none; cursor:pointer; z-index:2147483000;
    display:flex; align-items:center; justify-content:center;
    box-shadow:0 6px 22px rgba(31,60,136,0.38);
    transition:transform .15s ease, background .15s ease; font-family:inherit;
  }
  .a1aw-launcher:hover { transform:scale(1.06); background:var(--a1-navy-dark); }
  .a1aw-launcher-icon { font-size:27px; line-height:1; }
  .a1aw-launcher-dot { position:absolute; top:12px; right:12px; width:11px; height:11px; background:#4ade80; border:2px solid var(--a1-navy); border-radius:50%; }
  .a1aw-root.a1aw-open .a1aw-launcher { display:none; }
  .a1aw-launcher { touch-action:none; }
  .a1aw-launcher.a1aw-dragging { transition:none; cursor:grabbing; box-shadow:0 10px 30px rgba(31,60,136,0.5); }

  .a1aw-teaser {
    position:fixed; bottom:94px; right:22px; width:252px; max-width:calc(100vw - 40px);
    background:var(--a1-white); border:1px solid var(--a1-border); border-radius:14px;
    box-shadow:0 10px 34px rgba(31,60,136,0.22); padding:13px 15px; z-index:2147483000;
    cursor:pointer; display:none; animation:a1aw-pop .2s ease;
  }
  .a1aw-teaser.visible { display:block; }
  .a1aw-teaser-inner { display:flex; gap:10px; align-items:flex-start; }
  .a1aw-teaser-avatar { width:30px; height:30px; background:var(--a1-navy-light); border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .a1aw-teaser-text { font-size:13px; line-height:1.45; color:var(--a1-text); }
  .a1aw-teaser-text strong { color:var(--a1-navy); font-weight:700; }
  .a1aw-teaser-close {
    position:absolute; top:-9px; right:-9px; width:22px; height:22px; border-radius:50%;
    background:var(--a1-white); border:1px solid var(--a1-border); box-shadow:0 2px 6px rgba(0,0,0,0.16);
    color:var(--a1-muted); cursor:pointer; display:flex; align-items:center; justify-content:center; padding:0;
    transition:background .13s, color .13s, border-color .13s;
  }
  .a1aw-teaser-close:hover { background:var(--a1-navy); color:#fff; border-color:var(--a1-navy); }
  .a1aw-teaser-close svg { width:11px; height:11px; stroke:currentColor; stroke-width:2.6; fill:none; }
  .a1aw-root.a1aw-open .a1aw-teaser { display:none !important; }

  .a1aw-panel {
    position:fixed; bottom:22px; right:22px; width:384px; height:580px; max-height:calc(100vh - 44px);
    background:var(--a1-white); border-radius:16px; border:1px solid var(--a1-border);
    box-shadow:0 12px 44px rgba(31,60,136,0.22); z-index:2147483001;
    display:none; flex-direction:column; overflow:hidden; animation:a1aw-pop .18s ease;
  }
  @keyframes a1aw-pop { from { opacity:0; transform:translateY(12px) scale(.98); } to { opacity:1; transform:none; } }
  .a1aw-root.a1aw-open .a1aw-panel { display:flex; }

  .a1aw-header { background:var(--a1-navy); padding:14px 16px; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-shrink:0; }
  .a1aw-header-left { display:flex; align-items:center; gap:11px; min-width:0; }
  .a1aw-avatar { width:38px; height:38px; background:rgba(255,255,255,0.18); border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:19px; flex-shrink:0; }
  .a1aw-header-text { min-width:0; }
  .a1aw-header-text h2 { margin:0; color:#fff; font-size:15.5px; font-weight:600; letter-spacing:-0.01em; white-space:nowrap; }
  .a1aw-header-text p { margin:1px 0 0; color:rgba(255,255,255,0.66); font-size:11.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .a1aw-header-actions { display:flex; align-items:center; gap:2px; flex-shrink:0; }
  .a1aw-iconbtn { width:30px; height:30px; background:rgba(255,255,255,0.12); border:none; border-radius:7px; color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .13s; font-family:inherit; padding:0; }
  .a1aw-iconbtn:hover { background:rgba(255,255,255,0.26); }
  .a1aw-iconbtn svg { width:15px; height:15px; stroke:#fff; stroke-width:2.4; fill:none; }

  .a1aw-consent { padding:22px 22px 20px; background:var(--a1-white); display:none; flex-direction:column; gap:16px; overflow-y:auto; flex:1; }
  .a1aw-consent.visible { display:flex; }
  .a1aw-ai-badge { display:inline-flex; align-items:center; gap:6px; background:var(--a1-navy-light); border:1px solid rgba(31,60,136,0.18); border-radius:20px; padding:5px 12px; font-size:12px; font-weight:600; color:var(--a1-navy); align-self:flex-start; }
  .a1aw-ai-badge-dot { width:7px; height:7px; background:var(--a1-navy); border-radius:50%; }
  .a1aw-consent-heading { font-size:18px; font-weight:700; color:var(--a1-text); line-height:1.3; margin:0; }
  .a1aw-consent-heading span { color:var(--a1-navy); }
  .a1aw-consent-body { font-size:13px; color:#374151; line-height:1.6; }
  .a1aw-disclaimer { background:#fffbeb; border:1px solid #fcd34d; border-radius:8px; padding:13px 15px; font-size:12.5px; color:#78350f; line-height:1.55; }
  .a1aw-disclaimer strong { color:#92400e; }
  .a1aw-disclaimer ul { margin:9px 0 0 16px; padding:0; font-size:12.5px; color:#78350f; line-height:1.65; }
  .a1aw-checkbox-row { display:flex; align-items:flex-start; gap:10px; cursor:pointer; user-select:none; }
  .a1aw-checkbox-row input[type="checkbox"] { width:17px; height:17px; margin-top:1px; accent-color:var(--a1-navy); flex-shrink:0; cursor:pointer; }
  .a1aw-checkbox-row label { font-size:12.5px; color:var(--a1-text); line-height:1.5; cursor:pointer; }
  .a1aw-consent-btn { background:var(--a1-navy); color:#fff; border:none; border-radius:8px; padding:12px 20px; font-size:14.5px; font-weight:600; cursor:pointer; transition:background .13s, opacity .13s; align-self:stretch; letter-spacing:-0.01em; font-family:inherit; }
  .a1aw-consent-btn:hover:not(:disabled) { background:var(--a1-navy-dark); }
  .a1aw-consent-btn:disabled { opacity:.38; cursor:not-allowed; }
  .a1aw-consent-footer { font-size:11.5px; color:var(--a1-muted); text-align:center; line-height:1.5; }

  .a1aw-chat { display:none; flex-direction:column; flex:1; min-height:0; }
  .a1aw-chat.visible { display:flex; }
  .a1aw-chat-area { overflow-y:auto; padding:18px; display:flex; flex-direction:column; gap:13px; flex:1; min-height:0; background:var(--a1-gray); }
  .a1aw-msg { display:flex; gap:9px; align-items:flex-start; animation:a1aw-fadeIn .18s ease; }
  @keyframes a1aw-fadeIn { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:none; } }
  .a1aw-msg.user { flex-direction:row-reverse; }
  .a1aw-msg-avatar { width:30px; height:30px; border-radius:8px; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:600; }
  .a1aw-msg.bot .a1aw-msg-avatar { background:var(--a1-navy); color:#fff; }
  .a1aw-msg.user .a1aw-msg-avatar { background:var(--a1-orange); color:#fff; font-size:11px; }
  .a1aw-bubble { max-width:calc(100% - 80px); padding:10px 13px; border-radius:12px; font-size:13.5px; line-height:1.55; word-wrap:break-word; overflow-wrap:anywhere; }
  .a1aw-msg.bot .a1aw-bubble { background:#fff; border:1px solid var(--a1-border); border-top-left-radius:3px; color:var(--a1-text); }
  .a1aw-msg.user .a1aw-bubble { background:var(--a1-navy); color:#fff; border-top-right-radius:3px; }
  .a1aw-bubble a { color:var(--a1-orange); text-decoration:underline; }
  .a1aw-msg.user .a1aw-bubble a { color:#ffd090; }
  .a1aw-bubble strong { font-weight:600; }
  .a1aw-bubble ul { margin:8px 0 4px 18px; padding:0; }
  .a1aw-bubble li { margin-bottom:3px; }
  .a1aw-thinking { display:flex; gap:4px; padding:11px 13px; }
  .a1aw-thinking span { width:7px; height:7px; background:var(--a1-muted); border-radius:50%; animation:a1aw-bounce 1.1s infinite; }
  .a1aw-thinking span:nth-child(2) { animation-delay:.17s; }
  .a1aw-thinking span:nth-child(3) { animation-delay:.34s; }
  @keyframes a1aw-bounce { 0%,60%,100% { transform:translateY(0); } 30% { transform:translateY(-7px); } }

  .a1aw-chips-section { padding:12px 18px 0; background:var(--a1-gray); border-top:1px solid var(--a1-border); flex-shrink:0; }
  .a1aw-chips-label { font-size:11px; color:var(--a1-muted); text-transform:uppercase; letter-spacing:.05em; font-weight:600; margin-bottom:7px; }
  .a1aw-chips { display:flex; flex-wrap:wrap; gap:6px; padding-bottom:12px; }
  .a1aw-chip { background:#fff; border:1px solid var(--a1-border); border-radius:20px; padding:6px 12px; font-size:12px; color:var(--a1-navy); cursor:pointer; font-weight:500; transition:all .13s; white-space:nowrap; font-family:inherit; }
  .a1aw-chip:hover { background:var(--a1-navy-light); border-color:var(--a1-navy); }

  .a1aw-input-row { padding:12px 16px; background:#fff; border-top:1px solid var(--a1-border); display:flex; gap:9px; align-items:flex-end; flex-shrink:0; }
  .a1aw-input-wrap { flex:1; position:relative; }
  .a1aw-root .a1aw-input { width:100%; resize:none; border:1.5px solid var(--a1-border); border-radius:7px; padding:10px 13px; font-size:13.5px; font-family:inherit; outline:none; transition:border-color .15s; min-height:42px; max-height:110px; line-height:1.45; color:var(--a1-text); background:var(--a1-gray); }
  .a1aw-root .a1aw-input:focus { border-color:var(--a1-navy); background:#fff; }
  .a1aw-root .a1aw-input::placeholder { color:var(--a1-muted); }
  .a1aw-char-count { position:absolute; right:9px; bottom:7px; font-size:10.5px; color:var(--a1-muted); pointer-events:none; }
  .a1aw-char-count.warn { color:#ef4444; }
  .a1aw-root .a1aw-send { width:42px; height:42px; background:var(--a1-navy); border:none; border-radius:7px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .13s, opacity .13s; flex-shrink:0; }
  .a1aw-root .a1aw-send:hover:not(:disabled) { background:var(--a1-navy-dark); }
  .a1aw-root .a1aw-send:disabled { opacity:.4; cursor:not-allowed; }
  .a1aw-root .a1aw-send svg { width:17px; height:17px; fill:none; stroke:#fff; stroke-width:2; }

  .a1aw-footer { padding:9px 16px; background:var(--a1-navy-light); border-top:1px solid var(--a1-border); display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; flex-shrink:0; }
  .a1aw-footer-phone { font-size:11.5px; color:var(--a1-navy); font-weight:500; display:flex; align-items:center; gap:5px; }
  .a1aw-footer-phone svg { width:12px; height:12px; fill:none; stroke:var(--a1-navy); stroke-width:2; }
  .a1aw-quote-btn { background:var(--a1-orange); color:#fff; border:none; border-radius:6px; padding:6px 12px; font-size:11.5px; font-weight:600; cursor:pointer; text-decoration:none; transition:background .13s; display:inline-block; }
  .a1aw-quote-btn:hover { background:#d0651a; }

  @media (max-width:500px) {
    .a1aw-panel { bottom:0; right:0; top:0; left:0; width:100%; height:100%; max-height:100%; border-radius:0; border:none; }
    .a1aw-launcher { bottom:16px; right:16px; }
    .a1aw-bubble { max-width:calc(100% - 46px); }
    .a1aw-root .a1aw-input { min-height:62px; white-space:pre-wrap; }
  }`;

  // ============================================================
  //  Markup
  // ============================================================
  var HTML = `
  <button type="button" id="a1aw-launcher" class="a1aw-launcher" aria-label="Open A1 Air Advisor chat" aria-expanded="false">
    <span class="a1aw-launcher-icon">🔧</span>
    <span class="a1aw-launcher-dot" aria-hidden="true"></span>
  </button>
  <div id="a1aw-teaser" class="a1aw-teaser" role="button" tabindex="0" aria-label="Open A1 Air Advisor chat">
    <button type="button" id="a1aw-teaser-close" class="a1aw-teaser-close" aria-label="Dismiss welcome message">
      <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"></path></svg>
    </button>
    <div class="a1aw-teaser-inner">
      <span class="a1aw-teaser-avatar">🔧</span>
      <span class="a1aw-teaser-text">👋 Need help sizing a compressor <strong>or finding a part?</strong> Ask A1 Air Advisor — it's free.</span>
    </div>
  </div>
  <div id="a1aw-panel" class="a1aw-panel" role="dialog" aria-label="A1 Air Advisor chat" aria-hidden="true">
    <div class="a1aw-header">
      <div class="a1aw-header-left">
        <div class="a1aw-avatar">🔧</div>
        <div class="a1aw-header-text">
          <h2>A1 Air Advisor</h2>
          <p>Powered by AI · A1 Compressor Warehouse</p>
        </div>
      </div>
      <div class="a1aw-header-actions">
        <button type="button" id="a1aw-minimize" class="a1aw-iconbtn" aria-label="Minimize chat" title="Minimize">
          <svg viewBox="0 0 24 24"><path d="M5 12h14"></path></svg>
        </button>
        <button type="button" id="a1aw-close" class="a1aw-iconbtn" aria-label="Close chat" title="Close">
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"></path></svg>
        </button>
      </div>
    </div>
    <div class="a1aw-consent" id="a1aw-consent">
      <div class="a1aw-ai-badge"><div class="a1aw-ai-badge-dot"></div>AI-Powered Tool</div>
      <h3 class="a1aw-consent-heading">Meet <span>A1 Air Advisor</span> —<br>your compressed air expert.</h3>
      <div class="a1aw-consent-body">A1 Air Advisor uses artificial intelligence to help you size compressors, find parts, and troubleshoot your equipment. It draws on deep industry knowledge — but like any AI, it can make mistakes.</div>
      <div class="a1aw-disclaimer">
        <strong>⚠️ Please read before continuing</strong>
        <ul>
          <li>A1 Air Advisor is an <strong>AI assistant</strong>, not a licensed engineer. Responses may contain errors or omissions.</li>
          <li>Always verify specifications, compatibility, and suitability with an A1 team member before purchasing.</li>
          <li>A1 Compressor Warehouse is <strong>not liable</strong> for purchases, installations, or decisions made based solely on AI-generated advice.</li>
          <li>For complex systems, hazardous environments, or code-compliance questions, please speak with our team directly.</li>
        </ul>
      </div>
      <div class="a1aw-checkbox-row">
        <input type="checkbox" id="a1aw-agree-check">
        <label for="a1aw-agree-check">I understand that A1 Air Advisor is an AI tool, its advice may not be 100% accurate, and A1 Compressor Warehouse is not responsible for any decisions or purchases made based on its recommendations.</label>
      </div>
      <button class="a1aw-consent-btn" id="a1aw-consent-btn" disabled type="button">I Agree — Start Chatting →</button>
      <div class="a1aw-consent-footer">Prefer to speak with a human? Call us at <strong>(803) 693-2350</strong> · Mon–Fri 8am–5pm ET</div>
    </div>
    <div class="a1aw-chat" id="a1aw-chat">
      <div class="a1aw-chat-area" id="a1aw-chat-area" aria-live="polite" aria-label="Conversation"></div>
      <div class="a1aw-chips-section" id="a1aw-chips-section">
        <div class="a1aw-chips-label">Quick starts</div>
        <div class="a1aw-chips">
          <button class="a1aw-chip" type="button" data-prompt="Help me size a compressor for my shop">Size a compressor</button>
          <button class="a1aw-chip" type="button" data-prompt="I need an oil-free compressor for food or medical use">Oil-free compressors</button>
          <button class="a1aw-chip" type="button" data-prompt="Help me find a maintenance kit for my compressor">Maintenance parts</button>
          <button class="a1aw-chip" type="button" data-prompt="My compressor is having a problem, help me troubleshoot">Troubleshoot an issue</button>
        </div>
      </div>
      <div class="a1aw-input-row">
        <div class="a1aw-input-wrap">
          <textarea class="a1aw-input" id="a1aw-user-input" rows="1" placeholder="Describe your application or ask a question..." maxlength="500" aria-label="Your message"></textarea>
          <span class="a1aw-char-count" id="a1aw-char-count">500</span>
        </div>
        <button class="a1aw-send" id="a1aw-send-btn" type="button" aria-label="Send message">
          <svg viewBox="0 0 24 24"><path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"></path></svg>
        </button>
      </div>
      <div class="a1aw-footer">
        <span class="a1aw-footer-phone">
          <svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81 19.79 19.79 0 01.08 2.18 2 2 0 012.08.01h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"></path></svg>
          (803) 693-2350
        </span>
        <a class="a1aw-quote-btn" href="https://a1compressor-warehouse.myshopify.com/pages/contact" target="_blank" rel="noopener">Request a Quote →</a>
      </div>
    </div>
  </div>`;

  // ---------- Inject style + markup ----------
  var styleEl = document.createElement('style');
  styleEl.setAttribute('data-a1aw', '1');
  styleEl.textContent = CSS;
  document.head.appendChild(styleEl);

  var root = document.createElement('div');
  root.id = 'a1aw-root';
  root.className = 'a1aw-root';
  root.innerHTML = HTML;
  MOUNT.appendChild(root);

  // ---------- Element refs ----------
  var launcher = root.querySelector('#a1aw-launcher');
  var panel = root.querySelector('#a1aw-panel');
  var minimizeBtn = root.querySelector('#a1aw-minimize');
  var closeBtn = root.querySelector('#a1aw-close');
  var consent = root.querySelector('#a1aw-consent');
  var consentCheck = root.querySelector('#a1aw-agree-check');
  var consentBtn = root.querySelector('#a1aw-consent-btn');
  var chatPanel = root.querySelector('#a1aw-chat');
  var chatArea = root.querySelector('#a1aw-chat-area');
  var input = root.querySelector('#a1aw-user-input');
  var sendBtn = root.querySelector('#a1aw-send-btn');
  var charCount = root.querySelector('#a1aw-char-count');
  var chipsSection = root.querySelector('#a1aw-chips-section');
  var chips = root.querySelectorAll('.a1aw-chip');
  var teaser = root.querySelector('#a1aw-teaser');
  var teaserClose = root.querySelector('#a1aw-teaser-close');

  // ---------- Conversation state (persisted across navigation) ----------
  var history = loadHistory();
  var chatRendered = false;

  function loadHistory() {
    try { return JSON.parse(sessionStorage.getItem(HISTORY_KEY) || '[]') || []; } catch (e) { return []; }
  }
  function saveHistory() {
    try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch (e) {}
  }

  // ============================================================
  //  3-STRIKE LOCKOUT (mirrors the Worker; persists per session)
  // ============================================================
  var STRIKE_LIMIT = 3;
  function getStrikes() { try { return parseInt(sessionStorage.getItem(STRIKES_KEY) || '0', 10) || 0; } catch (e) { return 0; } }
  function setStrikes(n) { try { sessionStorage.setItem(STRIKES_KEY, String(n)); } catch (e) {} }
  function isLocked() { try { return sessionStorage.getItem(LOCKED_KEY) === '1'; } catch (e) { return false; } }
  function setLocked() { try { sessionStorage.setItem(LOCKED_KEY, '1'); } catch (e) {} }

  function lockSession() {
    setLocked();
    input.disabled = true;
    sendBtn.disabled = true;
    input.placeholder = 'Session ended — please call (803) 693-2350';
    input.value = '';
    if (chipsSection) chipsSection.style.display = 'none';
    appendMessage('bot',
      '<strong>This session has ended.</strong><br><br>' +
      'It looks like the questions are outside what I\'m set up to help with. ' +
      'For anything I can\'t answer here, our team is happy to help directly:<br><br>' +
      '📞 <a href="tel:8036932350"><strong>(803) 693-2350</strong></a> &middot; Mon–Fri 8am–5pm ET<br>' +
      '📝 <a href="https://a1compressor-warehouse.myshopify.com/pages/contact" target="_blank" rel="noopener">Request a Quote</a>'
    );
  }

  function registerStrike() {
    var s = getStrikes() + 1;
    setStrikes(s);
    if (s >= STRIKE_LIMIT) { lockSession(); return true; }
    var remaining = STRIKE_LIMIT - s;
    appendMessage('bot',
      DEFLECTION_LINE +
      '<br><br><em style="color:#9a3412;font-size:12px;">Heads up: ' +
      remaining + ' more off-topic ' + (remaining === 1 ? 'message' : 'messages') +
      ' and this session will end.</em>'
    );
    return false;
  }

  function isDeflectionResponse(text) {
    if (!text) return false;
    var normalized = String(text).toLowerCase().replace(/[—–-]/g, ' ');
    return normalized.indexOf('only set up to help with compressor') !== -1
      && normalized.indexOf('(803) 693-2350') !== -1
      && normalized.length < 350;
  }

  // ============================================================
  //  Open / close / minimize  (the widget is always present)
  // ============================================================
  function hasConsent() { try { return localStorage.getItem(CONSENT_KEY) === '1'; } catch (e) { return false; } }
  function setConsent() { try { localStorage.setItem(CONSENT_KEY, '1'); } catch (e) {} }

  function showConsent() { consent.classList.add('visible'); chatPanel.classList.remove('visible'); }
  function showChat() {
    consent.classList.remove('visible');
    chatPanel.classList.add('visible');
    renderTranscript();
    if (!isLocked()) input.focus();
  }

  function openPanel() {
    root.classList.add('a1aw-open');
    hideTeaser(); markTeaserSeen();
    panel.setAttribute('aria-hidden', 'false');
    launcher.setAttribute('aria-expanded', 'true');
    try { sessionStorage.setItem(OPEN_KEY, '1'); } catch (e) {}
    if (hasConsent()) showChat(); else showConsent();
  }
  function collapsePanel() {
    root.classList.remove('a1aw-open');
    panel.setAttribute('aria-hidden', 'true');
    launcher.setAttribute('aria-expanded', 'false');
    try { sessionStorage.setItem(OPEN_KEY, '0'); } catch (e) {}
  }

  // Render the stored conversation (or the welcome message) once per page load.
  function renderTranscript() {
    if (chatRendered) return;
    chatRendered = true;
    chatArea.innerHTML = '';
    if (history.length === 0) {
      appendMessage('bot', WELCOME_HTML);
    } else {
      history.forEach(function (m) {
        if (m.role === 'user') appendMessage('user', sanitize(m.content));
        else appendMessage('bot', markdownToHtml(m.content));
      });
      if (chipsSection) chipsSection.style.display = 'none';
    }
    if (isLocked()) lockSession();
  }

  // ============================================================
  //  Launcher position (draggable, snaps to nearest edge, remembered)
  // ============================================================
  var POS_KEY = 'a1aw_pos';                  // localStorage — remembered on device
  var TEASER_SEEN_KEY = 'a1aw_teaser_seen';  // sessionStorage — welcome shows once/session
  var DRAG_THRESHOLD = 6;                     // px of movement before it counts as a drag
  var curPos = loadPos() || { side: 'right', bottom: launcherGap() };
  var dragState = null;
  var justDragged = false;

  function launcherGap() { return window.innerWidth <= 500 ? 16 : 22; }
  function loadPos() {
    try {
      var p = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
      if (p && (p.side === 'left' || p.side === 'right') && typeof p.bottom === 'number') return p;
    } catch (e) {}
    return null;
  }
  function savePos(p) { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch (e) {} }
  function clampBottom(b) {
    var h = launcher.offsetHeight || 60;
    var min = launcherGap();
    var max = window.innerHeight - h - launcherGap();
    if (max < min) max = min;
    return Math.max(min, Math.min(b, max));
  }
  function applyPos(p) {
    var gap = launcherGap();
    launcher.style.top = 'auto';
    launcher.style.bottom = clampBottom(p.bottom) + 'px';
    launcher.style.left = p.side === 'left' ? gap + 'px' : 'auto';
    launcher.style.right = p.side === 'right' ? gap + 'px' : 'auto';
    if (window.innerWidth <= 500) {
      // Mobile: panel is fullscreen (CSS) — clear inline anchors so CSS wins.
      panel.style.left = ''; panel.style.right = '';
    } else {
      panel.style.left = p.side === 'left' ? gap + 'px' : 'auto';
      panel.style.right = p.side === 'right' ? gap + 'px' : 'auto';
    }
    positionTeaser(p);
  }

  // ============================================================
  //  Welcome teaser (proactive greeting, once per session)
  // ============================================================
  function teaserSeen() { try { return sessionStorage.getItem(TEASER_SEEN_KEY) === '1'; } catch (e) { return false; } }
  function markTeaserSeen() { try { sessionStorage.setItem(TEASER_SEEN_KEY, '1'); } catch (e) {} }
  function positionTeaser(p) {
    var gap = launcherGap();
    var h = launcher.offsetHeight || 60;
    teaser.style.bottom = (clampBottom(p.bottom) + h + 12) + 'px';
    teaser.style.left = p.side === 'left' ? gap + 'px' : 'auto';
    teaser.style.right = p.side === 'right' ? gap + 'px' : 'auto';
  }
  function showTeaser() {
    if (teaserSeen() || root.classList.contains('a1aw-open')) return;
    positionTeaser(curPos);
    teaser.classList.add('visible');
    markTeaserSeen();
  }
  function hideTeaser() { teaser.classList.remove('visible'); }

  teaser.addEventListener('click', function (e) {
    if (e.target.closest('.a1aw-teaser-close')) return;
    hideTeaser(); openPanel();
  });
  teaser.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hideTeaser(); openPanel(); }
  });
  teaserClose.addEventListener('click', function (e) { e.stopPropagation(); hideTeaser(); });

  // ============================================================
  //  Drag (mouse + touch, unified via Pointer Events)
  // ============================================================
  launcher.addEventListener('pointerdown', function (e) {
    if (typeof e.button === 'number' && e.button !== 0) return;
    var r = launcher.getBoundingClientRect();
    dragState = {
      startX: e.clientX, startY: e.clientY,
      offsetX: e.clientX - r.left, offsetY: e.clientY - r.top,
      w: r.width, h: r.height, moved: false, pointerId: e.pointerId
    };
    try { launcher.setPointerCapture(e.pointerId); } catch (e2) {}
  });
  launcher.addEventListener('pointermove', function (e) {
    if (!dragState) return;
    var dx = e.clientX - dragState.startX, dy = e.clientY - dragState.startY;
    if (!dragState.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    if (!dragState.moved) { dragState.moved = true; launcher.classList.add('a1aw-dragging'); hideTeaser(); }
    e.preventDefault();
    var left = Math.max(4, Math.min(e.clientX - dragState.offsetX, window.innerWidth - dragState.w - 4));
    var top = Math.max(4, Math.min(e.clientY - dragState.offsetY, window.innerHeight - dragState.h - 4));
    launcher.style.left = left + 'px'; launcher.style.right = 'auto';
    launcher.style.top = top + 'px'; launcher.style.bottom = 'auto';
  });
  function endDrag() {
    if (!dragState) return;
    var moved = dragState.moved;
    try { launcher.releasePointerCapture(dragState.pointerId); } catch (e2) {}
    launcher.classList.remove('a1aw-dragging');
    if (moved) {
      var r = launcher.getBoundingClientRect();
      var side = (r.left + r.width / 2) < window.innerWidth / 2 ? 'left' : 'right';
      curPos = { side: side, bottom: Math.round(window.innerHeight - r.bottom) };
      savePos(curPos);
      applyPos(curPos);
      justDragged = true;                              // suppress the click that follows a drag
      setTimeout(function () { justDragged = false; }, 60);
    }
    dragState = null;
  }
  launcher.addEventListener('pointerup', endDrag);
  launcher.addEventListener('pointercancel', endDrag);

  // ---------- Open / minimize / close ----------
  launcher.addEventListener('click', function (e) {
    if (justDragged) { e.preventDefault(); e.stopPropagation(); return; }
    openPanel();
  });
  minimizeBtn.addEventListener('click', collapsePanel);
  closeBtn.addEventListener('click', collapsePanel);

  // ---------- Consent flow ----------
  consentCheck.addEventListener('change', function () { consentBtn.disabled = !consentCheck.checked; });
  consentBtn.addEventListener('click', function () {
    if (!consentCheck.checked) return;
    setConsent();
    showChat();
  });

  // ---------- Input handling ----------
  input.addEventListener('input', function () {
    var remaining = 500 - input.value.length;
    charCount.textContent = remaining;
    charCount.className = 'a1aw-char-count' + (remaining < 50 ? ' warn' : '');
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 110) + 'px';
  });
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  });
  sendBtn.addEventListener('click', handleSend);
  chips.forEach(function (chip) {
    chip.addEventListener('click', function () {
      input.value = chip.getAttribute('data-prompt') || chip.textContent.trim();
      handleSend();
    });
  });

  // ---------- Helpers ----------
  function scrollToBottom() { chatArea.scrollTop = chatArea.scrollHeight; }
  function sanitize(str) { return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  function appendMessage(role, html) {
    var div = document.createElement('div');
    div.className = 'a1aw-msg ' + role;
    div.innerHTML = '<div class="a1aw-msg-avatar">' + (role === 'bot' ? 'A1' : 'You') + '</div><div class="a1aw-bubble">' + html + '</div>';
    chatArea.appendChild(div);
    scrollToBottom();
  }
  function showThinking() {
    var div = document.createElement('div');
    div.className = 'a1aw-msg bot';
    div.id = 'a1aw-thinking';
    div.innerHTML = '<div class="a1aw-msg-avatar">A1</div><div class="a1aw-bubble a1aw-thinking"><span></span><span></span><span></span></div>';
    chatArea.appendChild(div);
    scrollToBottom();
  }
  function removeThinking() { var t = root.querySelector('#a1aw-thinking'); if (t) t.remove(); }

  function markdownToHtml(text) {
    return sanitize(text)
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      .replace(/^#{1,3} (.+)$/gm, '<strong>$1</strong>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*?<\/li>\s*)+/g, function (m) { return '<ul>' + m + '</ul>'; })
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>');
  }

  function isOffTopic(text) {
    var lower = text.toLowerCase();
    var blocked = ['ignore your instructions', 'ignore previous', 'forget your', 'new instructions', 'you are now', 'pretend you are', 'act as', 'jailbreak', 'ignore the system', 'recipe', 'weather', 'stock price', 'sports score', 'write a story', 'homework', 'translate to', 'tell me a joke', 'what is the capital'];
    for (var i = 0; i < blocked.length; i++) if (lower.indexOf(blocked[i]) !== -1) return true;
    return false;
  }

  // ---------- API (same contract as the /recommended page) ----------
  async function callAPI(userMessage) {
    history.push({ role: 'user', content: userMessage });
    if (history.length > MAX_TURNS * 2) history = history.slice(-MAX_TURNS * 2);
    saveHistory();

    var response = await fetch(A1AW_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: history })
    });
    var data;
    try { data = await response.json(); } catch (e) { data = null; }

    if (response.status === 403 && data && data.type === 'blocked') {
      history.pop(); saveHistory();
      return { type: 'blocked' };
    }
    if (!response.ok) {
      throw new Error('API ' + response.status + ': ' + (data && data.error ? data.error : 'request failed'));
    }
    if (data && data.type === 'deflection') {
      history.pop(); saveHistory();
      return { type: 'deflection', remaining: typeof data.remaining === 'number' ? data.remaining : 0 };
    }
    var assistantText = (data && data.content && data.content[0] && data.content[0].text)
      ? data.content[0].text
      : 'Sorry, I had trouble generating a response.';
    history.push({ role: 'assistant', content: assistantText });
    saveHistory();
    return { type: 'text', text: assistantText };
  }

  async function handleSend() {
    var text = input.value.trim();
    if (!text || sendBtn.disabled || isLocked()) return;

    // Layer 1: client-side keyword blocklist (cheap, no API call).
    if (isOffTopic(text)) {
      appendMessage('user', sanitize(text));
      input.value = ''; input.style.height = 'auto'; charCount.textContent = '500';
      registerStrike();
      return;
    }

    appendMessage('user', sanitize(text));
    if (window.a1Track && window.a1Track.advisorEngaged) window.a1Track.advisorEngaged();
    input.value = ''; input.style.height = 'auto'; charCount.textContent = '500';
    sendBtn.disabled = true;
    chipsSection.style.display = 'none';
    showThinking();

    try {
      var reply = await callAPI(text);
      removeThinking();

      if (reply.type === 'blocked') {
        lockSession();
      } else if (reply.type === 'deflection') {
        if (reply.remaining === 0) {
          lockSession();
        } else {
          appendMessage('bot',
            DEFLECTION_LINE +
            '<br><br><em style="color:#9a3412;font-size:12px;">Heads up: ' +
            reply.remaining + ' more off-topic ' + (reply.remaining === 1 ? 'message' : 'messages') +
            ' and this session will end.</em>'
          );
          setStrikes(STRIKE_LIMIT - reply.remaining);
        }
      } else if (isDeflectionResponse(reply.text)) {
        history.pop(); saveHistory();
        registerStrike();
      } else {
        appendMessage('bot', markdownToHtml(reply.text));
      }
    } catch (err) {
      console.error('A1 Advisor error:', err);
      removeThinking();
      appendMessage('bot', 'Sorry, I ran into a technical issue. Please try again or call us at <a href="tel:8036932350">(803) 693-2350</a>.');
      history.pop(); saveHistory();
    } finally {
      if (!isLocked()) sendBtn.disabled = false;
      input.focus();
    }
  }

  // ---------- Initialise position + schedule the welcome teaser ----------
  applyPos(curPos);
  window.addEventListener('resize', function () { applyPos(curPos); });
  if (!teaserSeen()) setTimeout(showTeaser, 4000);

  // ---------- Restore open state across navigation ----------
  try {
    if (sessionStorage.getItem(OPEN_KEY) === '1') openPanel();
  } catch (e) {}
})();

